import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTheme, updateTheme, getStockBackgrounds, getVenue } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Save, Lock, Smartphone } from "lucide-react";

interface BgPadding {
  paddingTop: number; paddingBottom: number; paddingX: number;
  paddingTopDesktop: number; paddingBottomDesktop: number; paddingXDesktop: number;
}

function BgImagePicker({ value, onChange }: { value: string; onChange: (url: string, padding?: BgPadding) => void }) {
  const { data: backgrounds } = useQuery({ queryKey: ["stock-backgrounds"], queryFn: getStockBackgrounds });
  return (
    <div>
      <Label>Menü Arkaplan Görseli</Label>
      <div className="mt-2 flex gap-2 overflow-x-auto pb-2">
        <button
          type="button"
          className={`flex-shrink-0 w-16 h-24 rounded-lg border-2 flex items-center justify-center text-xs text-muted-foreground ${!value ? "border-primary" : "border-border"}`}
          onClick={() => onChange("")}
        >Yok</button>
        {backgrounds?.map((bg) => (
          <button
            key={bg._id}
            type="button"
            className={`flex-shrink-0 w-16 h-24 rounded-lg border-2 overflow-hidden relative ${value === bg.url ? "border-primary ring-2 ring-primary" : "border-border"}`}
            onClick={() => onChange(bg.url, {
              paddingTop: bg.paddingTop ?? 40, paddingBottom: bg.paddingBottom ?? 35, paddingX: bg.paddingX ?? 5,
              paddingTopDesktop: bg.paddingTopDesktop ?? 90, paddingBottomDesktop: bg.paddingBottomDesktop ?? 55, paddingXDesktop: bg.paddingXDesktop ?? 50,
            })}
            title={bg.name}
          >
            <img src={bg.url} alt={bg.name} className="w-full h-full object-cover" />
            {value === bg.url && <div className="absolute inset-0 bg-primary/20 flex items-center justify-center"><span className="text-white text-lg">✓</span></div>}
          </button>
        ))}
      </div>
    </div>
  );
}

const PRESETS = [
  { id: "default", name: "Default", bg: "#FFFFFF", primary: "#059669", text: "#0f172a", font: "Inter" },
  { id: "dark", name: "Dark", bg: "#1a1a1a", primary: "#10b981", text: "#f1f5f9", font: "Inter" },
  { id: "minimal", name: "Minimal", bg: "#fafafa", primary: "#000000", text: "#0f172a", font: "Inter" },
  { id: "elegant", name: "Elegant", bg: "#1c1917", primary: "#d4a574", text: "#fafaf9", font: "Playfair Display" },
  { id: "rustic", name: "Rustic", bg: "#fef3c7", primary: "#92400e", text: "#1c1917", font: "Lora" },
  { id: "neon", name: "Neon", bg: "#0f0f0f", primary: "#00ff88", text: "#f1f5f9", font: "Montserrat" },
  { id: "pastel", name: "Pastel", bg: "#fdf2f8", primary: "#ec4899", text: "#1c1917", font: "Poppins" },
  { id: "ocean", name: "Ocean", bg: "#0c4a6e", primary: "#38bdf8", text: "#f0f9ff", font: "Inter" },
  { id: "forest", name: "Forest", bg: "#14532d", primary: "#4ade80", text: "#f0fdf4", font: "Lora" },
  { id: "sunset", name: "Sunset", bg: "#fff7ed", primary: "#f97316", text: "#1c1917", font: "Poppins" },
  { id: "coffee", name: "Coffee", bg: "#292524", primary: "#d6a36e", text: "#fafaf9", font: "Lora" },
  { id: "lavender", name: "Lavender", bg: "#faf5ff", primary: "#a855f7", text: "#1c1917", font: "Poppins" },
];

const FONTS = ["Inter", "Playfair Display", "Poppins", "Montserrat", "Lora", "Dancing Script"];

// Preview is now iframe-based
export function ThemePage() {
  const { venueId } = useParams<{ venueId: string }>();
  const qc = useQueryClient();
  const { t } = useI18n();

  // Load Google Fonts for preview
  useEffect(() => {
    const families = FONTS.map(f => f.replace(/ /g, '+')).join('&family=');
    const id = 'theme-google-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${families}:wght@400;500;600;700;800&display=swap`;
      document.head.appendChild(link);
    }
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["theme", venueId],
    queryFn: () => getTheme(venueId!),
    enabled: !!venueId,
  });

  const [form, setForm] = useState({
    preset: "default",
    primaryColor: "#059669",
    bgColor: "#FFFFFF",
    fontFamily: "Inter",
    headerStyle: "standard",
    categoryLayout: "list-open",
    itemLayout: "list",
    menuListStyle: "tabs",
    showLogo: true,
    bgImage: "",
    bgPaddingTop: 40,
    bgPaddingBottom: 35,
    bgPaddingX: 5,
    bgPaddingTopDesktop: 90,
    bgPaddingBottomDesktop: 55,
    bgPaddingXDesktop: 50,
    showCover: true,
    coverType: "none" as "none" | "image" | "video",
    customCss: "",
  });

  useEffect(() => {
    if (data?.theme) {
      setForm({
        preset: data.theme.preset,
        primaryColor: data.theme.primaryColor,
        bgColor: data.theme.bgColor,
        fontFamily: data.theme.fontFamily,
        headerStyle: data.theme.headerStyle,
        categoryLayout: data.theme.categoryLayout ?? "list-open",
        itemLayout: data.theme.itemLayout ?? "list",
        menuListStyle: data.theme.menuListStyle ?? "tabs",
        bgImage: data.theme.bgImage ?? "",
        bgPaddingTop: data.theme.bgPaddingTop ?? 40,
        bgPaddingBottom: data.theme.bgPaddingBottom ?? 35,
        bgPaddingX: data.theme.bgPaddingX ?? 5,
        bgPaddingTopDesktop: data.theme.bgPaddingTopDesktop ?? 90,
        bgPaddingBottomDesktop: data.theme.bgPaddingBottomDesktop ?? 55,
        bgPaddingXDesktop: data.theme.bgPaddingXDesktop ?? 50,
        showLogo: data.theme.showLogo,
        showCover: data.theme.showCover,
        // Backward-compat: eski venue'lerde coverType undefined → eski
        // headerStyle/showCover'a göre türet. Yeni Kaydet ile DB'ye yazılır.
        coverType:
          (data.theme as unknown as { coverType?: "none" | "image" | "video" }).coverType
          ?? ((data.theme.headerStyle === "hero" && data.theme.showCover) ? "image" : "none"),
        customCss: data.theme.customCss ?? "",
      });
    }
  }, [data]);

  const { data: venue } = useQuery({
    queryKey: ["venue", venueId],
    queryFn: () => getVenue(venueId!),
    enabled: !!venueId,
  });

  const [iframeKey, setIframeKey] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Auto-refresh preview on form change (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setIframeKey(k => k + 1), 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [JSON.stringify(form)]);

  const saveMutation = useMutation({
    mutationFn: () => updateTheme(venueId!, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["theme", venueId] });
    },
    onError: (err) => {
      // Eskiden hata sessizce yutuluyordu → kullanıcı "kaydedildi" sanıyor,
      // sayfa yenilenince eski değeri görüp şaşırıyordu (Zod 400 vakası).
      const ax = err as { response?: { data?: { error?: unknown } }; message?: string };
      const msg = typeof ax.response?.data?.error === "string"
        ? ax.response.data.error
        : ax.message || "Kaydedilemedi";
      // Şimdilik basit alert; ileride toast pattern eklenebilir.
      alert(msg);
    },
  });

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>;
  }

  const allowedPresets = data?.allowedPresets ?? ["default", "dark", "minimal"];
  const canCustomize = data?.canCustomize ?? false;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("theme_settings")}</h1>
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          <Save className="mr-2 size-4" />
          {saveMutation.isPending ? t("saving") : t("save")}
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Left: Settings */}
        <div className="flex-1 max-w-xl space-y-6">
          {/* Presets */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("theme_select")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {PRESETS.map((preset) => {
                  const locked = !allowedPresets.includes(preset.id);
                  return (
                    <button
                      key={preset.id}
                      disabled={locked}
                      onClick={() => {
                        setForm({ ...form, preset: preset.id, primaryColor: preset.primary, bgColor: preset.bg });
                      }}
                      className={`relative rounded-xl border-2 p-3 text-left transition-all ${
                        form.preset === preset.id
                          ? "border-primary shadow-sm"
                          : "border-transparent hover:border-border"
                      } ${locked ? "opacity-50" : ""}`}
                    >
                      <div
                        className="mb-2 h-12 rounded-lg"
                        style={{ background: preset.bg, border: "1px solid #e5e5e5" }}
                      >
                        <div
                          className="ml-2 mt-2 h-4 w-8 rounded"
                          style={{ background: preset.primary }}
                        />
                      </div>
                      <p className="text-sm font-medium">{preset.name}</p>
                      {locked && <Lock className="absolute right-2 top-2 size-3 text-muted-foreground" />}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Customization */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{t("customize")}</CardTitle>
                {!canCustomize && (
                  <Badge variant="secondary">
                    <Lock className="mr-1 size-3" />
                    Pro Plan
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className={`space-y-4 ${!canCustomize ? "opacity-50 pointer-events-none" : ""}`}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("primary_color")}</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.primaryColor}
                      onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                      className="size-10 cursor-pointer rounded border"
                    />
                    <Input
                      value={form.primaryColor}
                      onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t("bg_color")}</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.bgColor}
                      onChange={(e) => setForm({ ...form, bgColor: e.target.value })}
                      className="size-10 cursor-pointer rounded border"
                    />
                    <Input
                      value={form.bgColor}
                      onChange={(e) => setForm({ ...form, bgColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label>{t("font")}</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {FONTS.map((font) => (
                    <Badge
                      key={font}
                      variant={form.fontFamily === font ? "default" : "outline"}
                      className="cursor-pointer"
                      style={{ fontFamily: `'${font}', sans-serif` }}
                      onClick={() => setForm({ ...form, fontFamily: font })}
                    >
                      {font}
                    </Badge>
                  ))}
                </div>
              </div>
              {/* Yeni header modeli: enum stiller kalktı, iki bağımsız kontrol:
                  - `showLogo` (aşağıdaki ayrı toggle) → logo görünür mü
                  - `coverType` (bu select) → header'da cover layer ne göstersin
                  Logo varsa cover'ın üzerinde overlay olarak çizilir.
                  Eski `headerStyle` alanı DB'de duruyor (geriye uyum) ama
                  artık dashboard'dan ayarlanmıyor. */}
              <div>
                <Label>{t("cover_layer")}</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {([
                    { id: "none", labelKey: "cover_layer_none" },
                    { id: "image", labelKey: "cover_layer_image" },
                    { id: "video", labelKey: "cover_layer_video" },
                  ] as const).map(({ id, labelKey }) => (
                    <Badge
                      key={id}
                      variant={form.coverType === id ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => setForm({ ...form, coverType: id })}
                    >
                      {t(labelKey)}
                    </Badge>
                  ))}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{t("cover_layer_hint")}</p>
              </div>
              <div>
                <Label>{t("menu_list_style")}</Label>
                <div className="flex gap-2 mt-1">
                  {[
                    { id: "tabs", labelKey: "menu_style_tabs" },
                    { id: "list", labelKey: "menu_style_list" },
                    { id: "grid", labelKey: "menu_style_grid" },
                  ].map((style) => (
                    <Badge
                      key={style.id}
                      variant={form.menuListStyle === style.id ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => setForm({ ...form, menuListStyle: style.id })}
                    >
                      {t(style.labelKey as Parameters<typeof t>[0])}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t("menu_list_style_hint")}</p>
              </div>
              <div>
                <Label>{t("category_layout")}</Label>
                <div className="flex gap-2 mt-1">
                  {[
                    { id: "list-open", labelKey: "list_open" },
                    { id: "accordion", labelKey: "accordion" },
                    { id: "tabs", labelKey: "tabs" },
                    { id: "grid", labelKey: "grid" },
                  ].map((layout) => (
                    <Badge
                      key={layout.id}
                      variant={form.categoryLayout === layout.id ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => setForm({ ...form, categoryLayout: layout.id })}
                    >
                      {t(layout.labelKey as Parameters<typeof t>[0])}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label>{t("item_layout")}</Label>
                <div className="flex gap-2 mt-1">
                  {[
                    { id: "list", labelKey: "list" },
                    { id: "card", labelKey: "card" },
                    { id: "compact", labelKey: "compact" },
                  ].map((layout) => (
                    <Badge
                      key={layout.id}
                      variant={form.itemLayout === layout.id ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => setForm({ ...form, itemLayout: layout.id })}
                    >
                      {t(layout.labelKey as Parameters<typeof t>[0])}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.showLogo}
                    onCheckedChange={(v) => setForm({ ...form, showLogo: v })}
                  />
                  <Label>{t("show_logo")}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.showCover}
                    onCheckedChange={(v) => setForm({ ...form, showCover: v })}
                  />
                  <Label>{t("show_cover")}</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Background Image - separate card, always accessible */}
          <Card>
            <CardContent className="pt-5">
              <BgImagePicker value={form.bgImage} onChange={(url, padding) => {
                setForm({
                  ...form,
                  bgImage: url,
                  bgPaddingTop: padding?.paddingTop ?? 40,
                  bgPaddingBottom: padding?.paddingBottom ?? 35,
                  bgPaddingX: padding?.paddingX ?? 5,
                  bgPaddingTopDesktop: padding?.paddingTopDesktop ?? 90,
                  bgPaddingBottomDesktop: padding?.paddingBottomDesktop ?? 55,
                  bgPaddingXDesktop: padding?.paddingXDesktop ?? 50,
                });
              }} />
            </CardContent>
          </Card>

          {/* Custom CSS - hidden for now */}

          {saveMutation.isSuccess && (
            <p className="text-sm text-primary">{t("theme_saved")}</p>
          )}
        </div>

        {/* Live Preview */}
        {venue?.slug && (
          <div className="lg:sticky lg:top-6 lg:self-start flex justify-center">
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Smartphone className="size-4" />
                  {t("live_preview")}
                </div>
                <button
                  className="text-xs text-primary hover:underline"
                  onClick={() => setIframeKey(k => k + 1)}
                >
                  {t("refresh_preview")}
                </button>
              </div>
              <div
                className="overflow-hidden rounded-[32px] border shadow-xl bg-white"
                style={{ width: 280, height: 607 }}
              >
                <div style={{ width: 280, height: 607, overflow: "hidden" }}>
                  <iframe
                    key={iframeKey}
                    src={`https://menu.heremenu.app/${venue.slug}#theme=${encodeURIComponent(JSON.stringify(form))}`}
                    style={{
                      width: 375,
                      height: 812,
                      border: "none",
                      transform: "scale(0.747)",
                      transformOrigin: "top left",
                    }}
                    title="Menu Preview"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
